// bully ignore[directory-kebab-case: component-directory-required requires this directory to literally equal the PascalCase file stem (AgentCell), and no rule enforces PascalCase .tsx filenames — kebab-casing both dir and file instead trips oxlint's unicorn/filename-case; no shape satisfies all three checks at once. follow-up: revisit if bully's directory-kebab-case gains a component-directory exemption.]
import ArticleOutlined from '@mui/icons-material/ArticleOutlined'
import React from 'react'
import styled from 'styled-components'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { accents, brand } from 'sutra/tokens/colors'

import { DetectedIssueIcon } from 'features/trend-detection/components/IssueShared'
import { GapMeasurementIcon } from 'features/trend-detection/features/benchmarking-agent/GapStrengthBadge'

export type AgentCellType = 'issue_detection' | 'benchmarking' | 'general'

export type AgentCellProps = {
  agentType: AgentCellType
  name: string
  isDefault: boolean
  isPaused?: boolean
}

const AGENT_TYPE_LABEL: Record<AgentCellType, string> = {
  issue_detection: 'Issue Detection',
  benchmarking: 'Benchmarking',
  general: 'Reports Agent',
}

const ICON_TILE_BG_ACTIVE = '#EDF1F6'
const ICON_TILE_BG_PAUSED = '#E9EAEB'
const ICON_COLOR_ACTIVE = accents.reflectionBlue[500]
const ICON_COLOR_PAUSED = '#717680'
// Icon size < tile size on purpose — the three icon shapes ship with
// different viewBox padding, so setting this to the tile size (36) would
// render them at visibly different sizes.
const ICON_SIZE = 24

const AgentTypeIcon: React.FC<{
  agentType: AgentCellType
  color: string
}> = ({ agentType, color }) => {
  if (agentType === 'benchmarking') {
    return (
      <GapMeasurementIcon
        direction="leading"
        color={color}
        width={ICON_SIZE}
        height={ICON_SIZE}
      />
    )
  }
  if (agentType === 'general') {
    return <ReportsIcon $color={color} />
  }
  return (
    <DetectedIssueIcon color={color} width={ICON_SIZE} height={ICON_SIZE} />
  )
}

export const AgentCell: React.FC<AgentCellProps> = ({
  agentType,
  name,
  isDefault,
  isPaused = false,
}) => (
  <Root>
    <IconTile $isPaused={isPaused}>
      <AgentTypeIcon
        agentType={agentType}
        color={isPaused ? ICON_COLOR_PAUSED : ICON_COLOR_ACTIVE}
      />
    </IconTile>
    <TextBlock>
      <NameRow>
        <NameContainer>
          <Tooltip content={name} side="top" align="start" delay={400}>
            <Name>{name}</Name>
          </Tooltip>
        </NameContainer>
        {isDefault ? <Chiclet $tone="gray">Default</Chiclet> : null}
      </NameRow>
      <TypeSubtitle>{AGENT_TYPE_LABEL[agentType]}</TypeSubtitle>
    </TextBlock>
  </Root>
)

const ReportsIcon = styled(ArticleOutlined)<{ $color: string }>`
  font-size: ${ICON_SIZE}px !important;
  color: ${({ $color }) => $color};
`

// The flex chain (Root → TextBlock → NameContainer) must all stretch so
// the DefaultBadge lands at the column's trailing edge instead of glued to
// the name. Any missing width: 100% / flex: 1 collapses the chain to
// content-width.
const Root = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  width: 100%;
`

const IconTile = styled.span<{ $isPaused: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 5.68px;
  background: ${({ $isPaused }) =>
    $isPaused ? ICON_TILE_BG_PAUSED : ICON_TILE_BG_ACTIVE};
  flex-shrink: 0;
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

const NameContainer = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

const Name = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${brand.stillNight[800]};
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TypeSubtitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: rgba(40, 54, 79, 0.6);
  line-height: 18px;
`
